import { Component, OnDestroy, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { AiChatService } from '../../../core/services/ai-chat.service'
import { AiChatApiService, OneGptMessage } from '../../../core/services/ai-chat-api.service'
import { OneIconComponent } from '../one-icon/one-icon.component'
import { Observable, Subscription } from 'rxjs'
import { Router, RouterModule } from '@angular/router'
import { GroupBySectionPipe } from './group-by-section.pipe'

interface QuickAction {
  id: string
  label: string
  message: string
  section: string
  condition?: () => boolean
}

@Component({
  selector: 'one-ai-chat-dialog',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    OneIconComponent,
    RouterModule,
    GroupBySectionPipe,
  ],
  templateUrl: './ai-chat-dialog.component.html',
  styleUrls: ['./ai-chat-dialog.component.scss']
})
export class AiChatDialogComponent implements OnInit, OnDestroy {
  message = ''
  isOpen: Observable<boolean>
  currentChatId: string | null = null
  messages: OneGptMessage[] = []
  isLoading = false
  quickActions: QuickAction[] = [
    {
      id: 'popup-spin-wheel',
      label: 'Spin Wheel',
      message: 'Create a spin wheel popup',
      section: 'Create Pop Ups',
      condition: () => this.messages.length === 0
    },
    {
      id: 'popup-with-reward',
      label: 'With Reward',
      message: 'Create a popup with a reward',
      section: 'Create Pop Ups',
      condition: () => this.messages.length === 0
    },
    {
      id: 'popup-without-reward',
      label: 'Without Reward',
      message: 'Create a popup without a reward',
      section: 'Create Pop Ups',
      condition: () => this.messages.length === 0
    },
    {
      id: 'email-with-reward',
      label: 'With Reward',
      message: 'Create an email with a reward',
      section: 'Create Emails',
      condition: () => this.messages.length === 0
    },
    {
      id: 'email-without-reward',
      label: 'Without Reward',
      message: 'Create an email without a reward',
      section: 'Create Emails',
      condition: () => this.messages.length === 0
    },
  ]
  visibleQuickActions: QuickAction[] = []
  private subscription = new Subscription()

  constructor(
    private aiChatService: AiChatService,
    private aiChatApiService: AiChatApiService,
    private router: Router,
  ) {
    this.isOpen = this.aiChatService.isOpen
  }

  ngOnInit() {
    this.subscription.add(
      this.isOpen.subscribe(isOpen => {
        if (isOpen && !this.currentChatId) {
          this.createNewChat()
        }
        this.moveCrispButton(isOpen ? 420 : 0, isOpen)
      })
    )
  }

  ngOnDestroy() {
    this.subscription.unsubscribe()
  }

  moveCrispButton(rightPx: number, open: boolean) {
    const crispBtn = document.querySelector('#crisp-chatbox [aria-label="Open chat"]') as HTMLElement
    if (crispBtn && open) {
      crispBtn.style.setProperty('right', `${rightPx}px`, 'important')
    } else if (crispBtn && !open) {
      crispBtn.style.setProperty('right', `0`)
    }
  }

  private createNewChat() {
    this.subscription.add(
      this.aiChatApiService.createNewChat().subscribe({
        next: (chat) => {
          console.log('Chat created:', chat)
          this.currentChatId = chat.id
          this.loadMessages()
          this.updateVisibleQuickActions()
        },
        error: (error) => {
          console.error('Error creating chat:', error)
        }
      })
    )
  }

  private loadMessages() {
    if (!this.currentChatId) return

    this.subscription.add(
      this.aiChatApiService.getMessagesByConversationId(this.currentChatId).subscribe({
        next: (response) => {
          console.log('Messages loaded:', response)
          this.messages = response?.messages ?? []
          this.updateVisibleQuickActions()
        },
        error: (error) => {
          console.error('Error loading messages:', error)
        }
      })
    )
  }

  private updateVisibleQuickActions() {
    this.visibleQuickActions = this.quickActions.filter(action => 
      !action.condition || action.condition()
    )
  }

  closeChat() {
    this.aiChatService.closeChat()
    this.currentChatId = null
    this.messages = []
  }

  sendMessage(customMessage?: string) {
    if ((!this.message.trim() && !customMessage) || !this.currentChatId) return
    
    const messageText = customMessage || this.message.trim()
    this.message = ''

    // Add user message immediately
    const userMessage: OneGptMessage = {
      id: Date.now().toString(),
      content: {
        message: messageText
      },
      role: 'user',
      created_at: new Date().toISOString()
    }
    this.messages.push(userMessage)
    this.scrollToBottom()
    this.updateVisibleQuickActions()

    console.log('Sending message to chat:', this.currentChatId, messageText)
    
    // Set loading to true while waiting for response
    this.isLoading = true
    
    this.subscription.add(
      this.aiChatApiService.sendMessage(this.currentChatId, messageText).subscribe({
        next: (response) => {
          console.log('Message sent, response:', response)
          // Add AI response to messages
          this.messages.push(response)
          this.scrollToBottom()
          this.isLoading = false
          this.updateVisibleQuickActions()
        },
        error: (error) => {
          console.error('Error sending message:', error)
          this.isLoading = false
        }
      })
    )
  }

  executeQuickAction(action: QuickAction) {
    this.sendMessage(action.message)
  }

  public navigateTo(url: string) {
    this.router.navigate([url])
    this.closeChat()
  }

  private scrollToBottom() {
    setTimeout(() => {
      const messagesContainer = document.querySelector('.ai-chat-messages')
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight
      }
    })
  }
} 